import { NullOr } from "@regal-voice/shared-types";
import { CAMPAIGN_STATE_OPTIONS, CampaignState, CampaignMessageType } from "@regal-voice/shared-types/entities";

import { BADGE_STATUS, type BadgeStatus } from "@regal-voice/ui";
export type VoicemailInstructionOption = keyof typeof VOICEMAIL_INSTRUCTION_OPTIONS;
export type DialingModeOption = keyof typeof DIALING_MODE_OPTIONS;

export const VOICEMAIL_INSTRUCTION_OPTIONS = {
    empty: "empty",
    personalized: "personalized",
    prerecorded: "prerecorded",
} as const;

export const DIALING_MODE_OPTIONS = {
    manual: "manual",
    automated: "automated",
    progressive: "progressive",
    predictive: "predictive",
} as const;

import { AuditableGridDisplay } from "./AuditableEntity";
import { CommunicationSource as CommunicationSources } from "./CommunicationSource";
import { Journey, Tag } from "./Journey";
import { PrerecordedVoiceMessage } from "./Recordings";
import { Segment } from "./Segment";

export type { CommunicationSources };

// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
export enum CampaignType {
    AUTOMATED = "automated",
    AGENT_TASK = "agent-task",
    TRIGGERED = "triggered", // will be removed when the flag is removed
    BLAST = "blast", // will be removed when the flag is removed, ensure DB no longer has blast campaign types.
}
// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
export enum VoicemailRecordingType {
    VOICEMAIL_DROP = "voicemail_drop",
    AGENT_VOICEMAIL = "agent_voicemail",
}
// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
export enum SortColumns {
    ID = "friendlyId",
    NAME = "name",
    UPDATED_AT = "updatedAt",
    UPDATED_BY = "updatedBy",
    CREATED_AT = "createdAt",
    CREATED_BY = "createdBy",
    MESSAGE_TYPE = "messageType",
    CAMPAIGN_TYPE = "type",
    STATE = "state",
    DIALING_MODE = "phoneCallCampaign->dialingModeDisplay",
    PRIORITY = "phoneCallCampaign->priority",
}

export type AttributesCampaign = {
    id: string;
    friendlyId: string;
    campaignName: string;
    campaignDescription?: string;
    campaignGoal?: string;
    script?: string;
    voicemailInstructions: VoicemailInstructionOption;
    eligibleOffer?: string;
    conversionEvent?: ConversionEvent;
    bypassOptIn: boolean;
    communicationSources?: CommunicationSources;
};

export type AttributesCampaignWithJourneyInfo = AttributesCampaign & {
    journeyName: string;
};

export interface PhoneCallCampaign {
    id: string;
    fromNumber: string;
    nickname?: NullOr<string>;
    goal?: NullOr<string>;
    eligibleOffer?: NullOr<string>;
    dialingModeDisplay: DialingModeOption;
    scriptUrl?: NullOr<string>;
    voicemailInstructions?: VoicemailInstructionOption;
    prerecordedVoiceMessage?: PrerecordedVoiceMessage;
    abandonedRecording?: PrerecordedVoiceMessage;
    greetingRecording?: PrerecordedVoiceMessage;
    defaultVoicemail?: boolean;
    priority?: number;
    priorityStrategy?: string;
    targetQueueSid?: string;
    maxParallelCalls?: number;
    predictiveCallAgentRatioCeiling?: number;
    recordingType?: VoicemailRecordingType;
    targetAbandonRate?: number;
    transferQueueSid?: string;
    transferRateCeiling?: number;
}

export type PhoneCallCampaignAddUpdatePayload =
    | Omit<PhoneCallCampaign, "prerecordedVoiceMessage">
    | { prerecordedVoiceMessage?: string };

export interface SMSCampaign {
    id: string;
    fromNumber: string;
    content: string;
    mediaURL?: string;
}

interface ConversionEvent {
    name: string;
    [k: string]: any;
}

export interface CampaignJourneyReference {
    journey: Partial<Journey>;
}

export interface Campaign {
    id: string;
    readonly friendlyId?: number;
    state: CampaignState;
    name: string;
    description: NullOr<string>;
    messageType: CampaignMessageType;
    type: CampaignType;
    conversionEvent?: ConversionEvent;
    bypassOptIn: boolean;
    smsCampaign?: SMSCampaign;
    phoneCallCampaign?: PhoneCallCampaign | null;
    campaignsJourneys?: CampaignJourneyReference[];
    segmentIds?: string[];
    segments?: Segment[];
    tags?: Tag[];
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    fromNumber?: string;
}

export interface MessagePreview {
    contactPhone: string;
    // this is actually the friendlyId, we have an endpoint that uses the friendlyId
    // to do the lookup. we should probbaly update the call to use a diffent end point
    // and update the type to be a string
    campaignId: number;
    brand: string;
    isTest?: boolean;
    isPreview?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PhoneEntry extends CommunicationSources {}

export const campaignStatusMapping: Record<CampaignState, { label: string; status: BadgeStatus }> = {
    [CAMPAIGN_STATE_OPTIONS.unset]: { label: "", status: BADGE_STATUS.OTHER },
    [CAMPAIGN_STATE_OPTIONS.draft]: { label: "Draft", status: BADGE_STATUS.PENDING },
    [CAMPAIGN_STATE_OPTIONS.ready]: { label: "Ready", status: BADGE_STATUS.ACTIVE },
    [CAMPAIGN_STATE_OPTIONS.delete]: { label: "Deleted", status: BADGE_STATUS.INACTIVE },
    [CAMPAIGN_STATE_OPTIONS.scheduled]: { label: "Scheduled", status: BADGE_STATUS.ACTIVE },
    [CAMPAIGN_STATE_OPTIONS.queued]: { label: "Queued", status: BADGE_STATUS.IN_PROGRESS },
    [CAMPAIGN_STATE_OPTIONS.sending]: { label: "Sending", status: BADGE_STATUS.IN_PROGRESS },
    [CAMPAIGN_STATE_OPTIONS.completed]: { label: "Completed", status: BADGE_STATUS.DONE },
    [CAMPAIGN_STATE_OPTIONS.paused]: { label: "Paused", status: BADGE_STATUS.INACTIVE },
    [CAMPAIGN_STATE_OPTIONS.cancelled]: { label: "Cancelled", status: BADGE_STATUS.INACTIVE },
};

export type CampaignGrid = Campaign & AuditableGridDisplay;

export type CampaignFilterItems = {
    createdByList: string[];
    updatedByList: string[];
};
